import React, { useRef } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import { ACCEPTED_SCAN_TYPES } from '../../lib/fileLimits';
import { UploadProgress } from '../UploadProgress';
import s from './AddNewDrawer.module.css';

const MANUAL = [
  {
    type: 'expense',
    title: 'Add Expense Manually',
    desc: 'Enter the expense details yourself, including payee, date, category and any additional information.',
  },
  {
    type: 'revenue',
    title: 'Add Revenue Manually',
    desc: 'Enter the revenue details yourself, including payer, date, category and any additional information.',
  },
];

// The first panel of the add flow: how do you want to enter this.
//
// `scanEnabled` is a prop rather than an import of `isReceiptOcrConfigured`
// from src/lib/receiptOcr.js (#112, #113): that module reads `import.meta.env`,
// which Babel cannot parse, and a leaf component reading build-time
// configuration is the leak. ExpensesPage.jsx, the composition root, imports
// the OCR client to hand `extractReceipt` to useExpenseEntryForm (#106) and
// passes the flag from the same import, so whether the scan option is offered
// is assertable in a test.
//
// "Add Family Transaction" is ours, not MMS's: "Add Revenue" with a free-text
// Payer is not the family's account. It hands off to the family ledger's own
// drawer (AddTransactionDrawer), which asks for the family itself.
export default function AddNewDrawer({
  open, onClose, onPickType, onFamilyTransaction, onScan, scanning, error, scanEnabled, scanUpload = null,
}) {
  const receiptRef = useRef(null);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add Expense or Other Revenue"
      width={560}
      footer={<><FooterSpacer /><Button variant="secondary" onClick={onClose}>Cancel</Button></>}
    >
      <div className={s.body}>
        {MANUAL.map((o) => (
          <button key={o.type} className={s.choice} onClick={() => onPickType(o.type)}>
            <div className={s.choiceTitle}>{o.title}</div>
            <div className={s.choiceDesc}>{o.desc}</div>
          </button>
        ))}

        {onFamilyTransaction ? (
          <button className={s.choice} onClick={onFamilyTransaction}>
            <div className={s.choiceTitle}>Add Family Transaction</div>
            <div className={s.choiceDesc}>
              Record a payment, charge, discount or refund on a family&rsquo;s account.
            </div>
          </button>
        ) : null}

        {scanEnabled ? (
          <button
            className={`${s.choice} ${scanning ? s.choiceBusy : ''}`}
            disabled={scanning}
            onClick={() => receiptRef.current?.click()}
          >
            <div className={s.choiceTitle}>{scanning ? 'Reading receipt…' : 'Scan a Receipt'}</div>
            <div className={s.choiceDesc}>
              Upload a photo or PDF of a receipt and we will fill in the details for you to check.
            </div>
          </button>
        ) : null}

        <input
          ref={receiptRef}
          type="file"
          /* The OCR service's list, not the store's — this input feeds
             `extractReceipt`, and the reader takes photographs and PDFs. The
             attachment picker in AttachmentField.jsx takes ACCEPTED_FILE_TYPES,
             which is wider, and the two are different on purpose (#113). */
          accept={ACCEPTED_SCAN_TYPES}
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onScan(f); }}
        />

        {/* The receipt going up beside the reader (#612), as every upload
            shows itself. No ×: the reading and the upload are one action
            here, and the drawer's Cancel is how to leave it. */}
        {scanning && scanUpload ? <UploadProgress item={scanUpload} /> : null}

        {error ? <div className={s.error}>{error}</div> : null}

      </div>
    </Drawer>
  );
}
